import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { RuleGraphConditionSubject } from "@intelligems/ig-types";
import { RuleGraphLaneDot } from "./RuleGraphLaneDot";
import { RULE_GRAPH_CONDITION_PHRASES } from "./ruleGraphConditionPhrases";

type RuleGraphLaneDotState = "default" | "filled" | "pending";

type RuleGraphLaneLabelType = "when" | "fallback" | "fallback-when";

/**
 * The row-header sentence per condition subject. `when` and `fallbackWhen`
 * lead into the condition chip; `whenAnything` finishes the sentence itself
 * for a row with no chip. Only the cart's `whenAnything` is a real state (an
 * always-matching branch, or the input heading a branchless rule); the
 * others are a defensive floor, since the prompts forbid a non-cart subject
 * on a branch with nothing to chip.
 */
const laneLabelPhrase = (
  type: RuleGraphLaneLabelType,
  hasCondition: boolean,
  subject: RuleGraphConditionSubject
): string => {
  if (type === "fallback") {
    return "If nothing matches then";
  }
  const phrases = RULE_GRAPH_CONDITION_PHRASES[subject];
  if (type === "fallback-when") {
    return phrases.fallbackWhen;
  }
  return hasCondition ? phrases.when : phrases.whenAnything;
};

export const RuleGraphLaneLabel = ({
  type = "when",
  state = "default",
  color,
  hasCondition = false,
  subject = "cart",
  children,
}: {
  /**
   * "when": an entry row, gated by its own condition. "fallback": a row that
   * runs because the one above came up empty. "fallback-when": a fallback row
   * that ALSO carries a condition of its own, so the header composes both —
   * "If nothing matches and cart contains <chip>" — instead of hanging a
   * condition chip off "then", which reads as the fallback's output.
   */
  type?: RuleGraphLaneLabelType;
  /**
   * Which request context the chip is about; picks the sentence around it
   * ("When cart contains" vs "When delivery is"). Defaults to the cart, the
   * subject every graph spoke before `meta.conditionSubject` existed.
   */
  subject?: RuleGraphConditionSubject;
  state?: RuleGraphLaneDotState;
  /** The row's text color, so a ghosted lane fades its sentence too. */
  color?: string;
  /**
   * Whether a condition chip (or its skeleton stand-in) follows the phrase.
   * An always-matching row has no targeting value to chip, and "When cart
   * contains" alone dangles as an unfinished sentence — so the phrase
   * absorbs the missing chip and reads "When cart contains anything". The
   * fallback phrases are unaffected: "If nothing matches then" already ends
   * on a complete thought, and "…and cart contains" is only ever chosen for a
   * row that HAS a condition. Defaults to false so a caller that promises no
   * chip can never leave the sentence hanging.
   */
  hasCondition?: boolean;
  /** Rendered inline after the bold header text (the rule's own label). */
  children?: ReactNode;
}) => {
  const phrase = laneLabelPhrase(type, hasCondition, subject);

  return (
    <Box sx={{ alignItems: "center", display: "flex", gap: 2, minWidth: 0 }}>
      <RuleGraphLaneDot state={state} />
      <Typography
        sx={{
          color,
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        {phrase}
      </Typography>
      {children}
    </Box>
  );
};
